import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { LeadFormFields } from "./LeadFormFields";
import { useSite } from "@/context/SiteContext";
import { BRAND_NAME } from "@/constants/site";

export const LeadFormDialog = ({ open, onOpenChange, context, categories }) => {
  const { settings } = useSite();
  const preset = context
    ? {
        use_case: context.categorySlug || "",
        message: context.featureTitle
          ? `Interested in: ${context.featureTitle}. Tell us about your environment…`
          : "",
      }
    : undefined;

  const headerTitle = context?.featureTitle
    ? `Request a live demo of ${context.featureTitle}`
    : `${settings?.lead_form_title_lead || "See Smarter Spaces running on"} ${settings?.lead_form_title_highlight || "your camera feed."}`;
  const headerDescription = context?.featureTitle
    ? `Talk to ${settings?.brand_name || BRAND_NAME} about deploying ${context.featureTitle} on your cameras.`
    : "Share a few details and we'll set up a 30-minute live walkthrough on your reference camera location.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="lead-modal"
        className="max-w-2xl p-0 overflow-hidden bg-white border-neutral-200 [&>button.absolute]:hidden"
      >
        {/* Accessible title + description - visually styled below, but Radix needs them present */}
        <DialogTitle className="sr-only">{headerTitle}</DialogTitle>
        <DialogDescription className="sr-only">{headerDescription}</DialogDescription>

        <div className="bg-[#0A0A0A] text-white px-6 py-5 relative">
          <DialogClose
            data-testid="lead-modal-close"
            aria-label="Close"
            className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 border border-white/15 text-white hover:bg-brand hover:border-brand hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-[#0A0A0A]"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </DialogClose>

          <span className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-brand-accent">
            {settings?.lead_form_eyebrow || `Talk to ${BRAND_NAME}`}
          </span>
          <h2
            data-testid="lead-modal-title"
            className="font-display mt-1.5 text-2xl sm:text-3xl font-black tracking-tight leading-tight pr-12"
          >
            {context?.featureTitle ? (
              <>
                Request a live demo of{" "}
                <span className="text-brand-accent">{context.featureTitle}</span>
              </>
            ) : (
              <>
                {settings?.lead_form_title_lead || "See Smarter Spaces running on"}{" "}
                <span className="text-brand-accent">
                  {settings?.lead_form_title_highlight || "your camera feed."}
                </span>
              </>
            )}
          </h2>
          {context?.categoryName && (
            <p className="mt-2 font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-400">
              Use case · {context.categoryName}
            </p>
          )}
        </div>
        <div className="p-6 sm:p-7 max-h-[70vh] overflow-y-auto">
          <div className="mb-5 flex flex-wrap items-center gap-3 pb-4 border-b border-neutral-100">
            <Link
              to="/pricing"
              data-testid="lead-modal-pricing-cta"
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-neutral-800 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              Get pricing
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M5 12h14" /><path d="M13 5l7 7-7 7" />
              </svg>
            </Link>
            <p className="text-xs text-neutral-500 flex-1 min-w-[180px]">
              Want a number first? Try our live calculator.
            </p>
          </div>
          <LeadFormFields
            categories={categories}
            preset={preset}
            onSubmitted={() => onOpenChange(false)}
            ctaLabel="Send request"
            testidPrefix="modal-"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
